/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {CRUDPermissions, Device} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {SwapIcon} from '@ion/dls/src/icons/SwapIcon';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import * as actions from './state';

// defines the device row editor prop definitions ...
export interface DeviceRowEditorTypes {
  device: Device;
  refreshDevice?: boolean;
  showViewAction?: boolean;
  showEditAction?: boolean;
  showAuditAction?: boolean;
  showDeleteAction?: boolean;
  showSwapAction?: boolean;
  devicesPermissions?: CRUDPermissions;
}

/**
 * Defines the DeviceRowEditor component. This component contains the cell actions
 * for Devices grid.
 */
export const DeviceRowEditor = ({
  device,
  devicesPermissions,
  refreshDevice = false,
  showViewAction = false,
  showEditAction = false,
  showAuditAction = false,
  showDeleteAction = false,
  showSwapAction = false
}: DeviceRowEditorTypes) => {
  const dispatch    = useDispatch();
  const cellMenuRef = useRef(null);
  const {
    hasAddPermission,
    hasReadPermission,
    hasUpdatePermission,
    hasDeletePermission
  } = devicesPermissions || {};

  function closeMenu() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const onAction = function(type: string) {
    return function() {
      closeMenu();
      dispatch(actions[type](device));
    };
  };

  const onView = useCallback(() => {
    closeMenu();
    dispatch(actions.viewDevice(device, refreshDevice));
    dispatch(actions.loadDeviceDetail(device));
    dispatch(actions.viewAdminDevice(device));
  }, [device, refreshDevice]);

  const onEdit = useCallback(() => {
    closeMenu();
    dispatch(actions.editAdminDevice(device));
    dispatch(actions.loadDeviceDetail(device));
  }, [device]);

  const onSwap = useCallback(() => {
    closeMenu();
    if (device?.groups?.length || device?.asset?.href || isSelectedDeviceSCI(device)) {
      dispatch(actions.swapAdminDeviceConfirm(device));
    } else {
      dispatch(actions.swapAdminDevice(device));
    }
  }, [device]);

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div>
          {showViewAction && (
            <CellMenuItem icon={<ViewRecordIcon />}
              disabled={showAuditAction && !hasReadPermission}
              onClick={onView}
              label={translate('admin-devices:grid.menu.viewDevice')} />
          )}
          {showEditAction && (
            <CellMenuItem icon={<MapEditIcon />}
              disabled={showAuditAction && !hasUpdatePermission}
              onClick={onEdit}
              label={translate('admin-devices:grid.menu.editDevice')} />
          )}
          {showAuditAction && (
            <CellMenuItem icon={<NotificationTimeIcon />}
              onClick={onAction('auditDevices')}
              label={translate('admin-devices:grid.menu.auditLog')} />
          )}
          {(showSwapAction) && (
            <CellMenuItem icon={<SwapIcon />}
              disabled={showAuditAction && !hasAddPermission}
              onClick={onSwap}
              label={translate('admin-devices:grid.menu.swapDevice')} />
          )}
          {(showDeleteAction) && (
            <CellMenuItem icon={<SystemDeleteIcon />}
              disabled={showAuditAction && !hasDeletePermission}
              onClick={onAction('deleteAdminDevice')}
              label={translate('admin-devices:grid.menu.deleteDevice')} />
          )}
        </div>
      } />
    </>
  );
};
export default DeviceRowEditor;
